import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";

import Combox, { ComboxItemData } from "~/components/shared/Combox";
import { getJson } from "~/lib/http";

interface RemoteSearchableInputSelectProps {
  name: string;
  selectedItems?: ComboxItemData[] | null;
  searchUrl: string;
  placeholder?: string;
  required?: boolean;
  isMulti?: boolean;
  minimumSearchLength?: number;
  searchPrompt?: string;
  emptyState?: string;
}

const DEFAULT_MINIMUM_SEARCH_LENGTH = 2;
const VISIBLE_ROWS = 3;
const OPTION_ROW_HEIGHT = 32;
const LIST_VERTICAL_PADDING = 16;
const LIST_ROW_GAP = 1;

const getListHeight = (visibleRows: number) => {
  return (
    LIST_VERTICAL_PADDING +
    visibleRows * OPTION_ROW_HEIGHT +
    Math.max(visibleRows - 1, 0) * LIST_ROW_GAP
  );
};

const LoadingState = () => {
  return (
    <div className="flex flex-col gap-px">
      {["w-2/3", "w-5/6", "w-1/2"].map((widthClass) => (
        <div key={widthClass} className="px-2 py-1">
          <div className={`h-6 animate-pulse rounded-sm bg-gray-200 ${widthClass}`} />
        </div>
      ))}
    </div>
  );
};

const buildSearchUrl = (searchUrl: string, query: string) => {
  const [urlWithoutHash, hash = ""] = searchUrl.split("#", 2);
  const [path, existingQuery = ""] = urlWithoutHash.split("?", 2);
  const searchParams = new URLSearchParams(existingQuery);

  searchParams.set("q", query);

  const nextQuery = searchParams.toString();

  return `${path}${nextQuery ? `?${nextQuery}` : ""}${hash ? `#${hash}` : ""}`;
};

const mergeItems = (
  selectedValues: string[],
  knownItems: Map<string, ComboxItemData>,
  searchResults: ComboxItemData[],
  searchQuery: string,
) => {
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const mergedItems = selectedValues
    .map((value) => knownItems.get(value))
    .filter((item): item is ComboxItemData => {
      if (!item) {
        return false;
      }

      if (!normalizedSearchQuery) {
        return true;
      }

      return item.label.toLowerCase().includes(normalizedSearchQuery);
    });

  for (const item of searchResults) {
    if (!mergedItems.some((existingItem) => existingItem.value === item.value)) {
      mergedItems.push(item);
    }
  }

  return mergedItems;
};

const RemoteSearchableInputSelect = ({
  name,
  selectedItems,
  searchUrl,
  placeholder = "Search",
  required = false,
  isMulti = false,
  minimumSearchLength = DEFAULT_MINIMUM_SEARCH_LENGTH,
  searchPrompt,
  emptyState = "No results found",
}: RemoteSearchableInputSelectProps) => {
  const resolvedSelectedItems = useMemo(() => selectedItems ?? [], [selectedItems]);
  const [selectedValues, setSelectedValues] = useState(() =>
    resolvedSelectedItems.map((item) => item.value),
  );
  const [searchResults, setSearchResults] = useState<ComboxItemData[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [knownItems, setKnownItems] = useState(
    () => new Map(resolvedSelectedItems.map((item) => [item.value, item])),
  );
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setSelectedValues(resolvedSelectedItems.map((item) => item.value));
    setKnownItems(new Map(resolvedSelectedItems.map((item) => [item.value, item])));
  }, [resolvedSelectedItems]);

  const runSearch = useCallback(
    async (query: string) => {
      const trimmedQuery = query.trim();

      if (trimmedQuery.length < minimumSearchLength) {
        abortControllerRef.current?.abort();
        setSearchResults([]);
        setLoading(false);
        return;
      }

      const controller = new AbortController();
      abortControllerRef.current?.abort();
      abortControllerRef.current = controller;
      setLoading(true);

      try {
        const results = await getJson<ComboxItemData[]>(buildSearchUrl(searchUrl, trimmedQuery), {
          signal: controller.signal,
          headers: {
            Accept: "application/json",
          },
        });

        setSearchResults(results);
        setKnownItems((currentItems) => {
          const nextItems = new Map(currentItems);

          for (const item of results) {
            nextItems.set(item.value, item);
          }

          return nextItems;
        });
      } catch (error) {
        if (!(error instanceof Error && error.name === "AbortError")) {
          setSearchResults([]);
        }
      } finally {
        if (abortControllerRef.current === controller) {
          setLoading(false);
        }
      }
    },
    [minimumSearchLength, searchUrl],
  );

  const debouncedSearch = useMemo(
    () => debounce((query: string) => void runSearch(query), 250),
    [runSearch],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      abortControllerRef.current?.abort();
    };
  }, [debouncedSearch]);

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      debouncedSearch(query);
    },
    [debouncedSearch],
  );

  const handleValueChange = useCallback((value: string) => {
    setSelectedValues(value ? [value] : []);
  }, []);

  const items = mergeItems(selectedValues, knownItems, searchResults, searchQuery);
  const knownItemsForCombox = mergeItems(selectedValues, knownItems, searchResults, "");
  const selectedValue = selectedValues[0] || "";

  const resolvedEmptyState =
    searchQuery.trim().length < minimumSearchLength
      ? searchPrompt ?? `Type at least ${minimumSearchLength} characters to search`
      : emptyState;

  const listHeight = getListHeight(VISIBLE_ROWS);

  return (
    <Combox
      multiple={isMulti}
      value={isMulti ? undefined : selectedValue}
      onValueChange={isMulti ? undefined : handleValueChange}
      values={isMulti ? selectedValues : undefined}
      onValuesChange={isMulti ? setSelectedValues : undefined}
      items={items}
      knownItems={knownItemsForCombox}
      hiddenInputName={name}
      loading={loading}
      empty_state={resolvedEmptyState}
      filterItems={false}
      onSearchChange={handleSearchChange}
      triggerProps={{
        placeholder,
        clearable: !required,
        className: "w-full justify-between text-left",
        subtle: false,
      }}
      popoverProps={{ side: "top", avoidCollisions: false }}
      listProps={{
        loadingState: <LoadingState />,
        showEmptyStateWhenNoItems: true,
        style: {
          maxHeight: listHeight,
          flex: "0 0 auto",
          overflowY: "auto",
        },
      }}
      filterProps={{ placeholder }}
      disabled={false}
    />
  );
};

export default RemoteSearchableInputSelect;
